import React, { createContext, useContext, useReducer, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Product } from '../types';

// Interfaces
export interface FavoriteItem {
  id: number; // Este será el ID del producto para simplificar
  producto: Product;
  addedAt: string;
}

export interface FavoritesState {
  items: FavoriteItem[];
  isLoading: boolean;
  itemsCount: number;
}

// Actions
type FavoritesAction =
  | { type: 'LOADING' }
  | { type: 'LOAD_FAVORITES'; payload: FavoriteItem[] }
  | { type: 'ADD_FAVORITE'; payload: Product }
  | { type: 'REMOVE_FAVORITE'; payload: number }
  | { type: 'CLEAR_FAVORITES' };

// Reducer
const favoritesReducer = (state: FavoritesState, action: FavoritesAction): FavoritesState => {
  console.log('❤️ FavoritesReducer: Action dispatched:', action.type);
  
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true };
      
    case 'LOAD_FAVORITES':
      return {
        ...state,
        items: action.payload,
        itemsCount: action.payload.length,
        isLoading: false,
      };
      
    case 'ADD_FAVORITE':
      const newFavorite: FavoriteItem = {
        id: action.payload.id,
        producto: action.payload,
        addedAt: new Date().toISOString(),
      };
      
      const updatedItemsAdd = [...state.items, newFavorite];
      
      return {
        ...state,
        items: updatedItemsAdd,
        itemsCount: updatedItemsAdd.length,
      };
      
    case 'REMOVE_FAVORITE':
      const filteredItems = state.items.filter(item => item.id !== action.payload);
      
      return {
        ...state,
        items: filteredItems,
        itemsCount: filteredItems.length,
      };
      
    case 'CLEAR_FAVORITES':
      return {
        ...state,
        items: [],
        itemsCount: 0,
      };
      
    default:
      return state;
  }
};

// Context Interface
interface FavoritesContextType {
  state: FavoritesState;
  addToFavorites: (product: Product) => Promise<void>;
  removeFromFavorites: (productId: number) => Promise<void>;
  isFavorite: (productId: number) => boolean;
  clearFavorites: () => Promise<void>;
  loadFavorites: () => Promise<void>;
}

// Context
const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

// Storage keys
const FAVORITES_STORAGE_KEY = '@MotoMarketGT:favorites';

// Provider
export const FavoritesProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(favoritesReducer, {
    items: [],
    isLoading: false,
    itemsCount: 0,
  });

  // Cargar favoritos desde AsyncStorage
  const loadFavorites = async () => {
    try {
      dispatch({ type: 'LOADING' });
      console.log('❤️ FavoritesContext: Cargando favoritos desde AsyncStorage...');
      
      const stored = await AsyncStorage.getItem(FAVORITES_STORAGE_KEY);
      if (stored) {
        const favorites = JSON.parse(stored);
        console.log('❤️ FavoritesContext: Favoritos cargados:', favorites.length);
        dispatch({ type: 'LOAD_FAVORITES', payload: favorites });
      } else {
        console.log('❤️ FavoritesContext: No hay favoritos guardados');
        dispatch({ type: 'LOAD_FAVORITES', payload: [] });
      }
    } catch (error) {
      console.error('❌ Error cargando favoritos:', error);
      dispatch({ type: 'LOAD_FAVORITES', payload: [] });
    }
  };

  // Guardar favoritos en AsyncStorage
  const saveFavorites = async (favorites: FavoriteItem[]) => {
    try {
      await AsyncStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
      console.log('✅ FavoritesContext: Favoritos guardados exitosamente');
    } catch (error) {
      console.error('❌ Error guardando favoritos:', error);
    }
  };

  // Agregar a favoritos
  const addToFavorites = async (product: Product) => {
    try {
      console.log('❤️ FavoritesContext: Agregando a favoritos:', product.nombre);
      
      // Verificar si ya está en favoritos
      if (isFavorite(product.id)) {
        console.log('⚠️ FavoritesContext: Producto ya está en favoritos');
        return;
      }
      
      dispatch({ type: 'ADD_FAVORITE', payload: product });
      
    } catch (error) {
      console.error('❌ Error agregando a favoritos:', error);
    }
  };

  // Remover de favoritos
  const removeFromFavorites = async (productId: number) => {
    try {
      console.log('💔 FavoritesContext: Removiendo de favoritos:', productId);
      dispatch({ type: 'REMOVE_FAVORITE', payload: productId });
    } catch (error) {
      console.error('❌ Error removiendo de favoritos:', error);
      throw error;
    }
  };

  // Verificar si un producto está en favoritos
  const isFavorite = (productId: number): boolean => {
    return state.items.some(item => item.id === productId);
  };

  // Limpiar todos los favoritos
  const clearFavorites = async () => {
    try {
      console.log('🗑️ FavoritesContext: Limpiando todos los favoritos');
      dispatch({ type: 'CLEAR_FAVORITES' });
    } catch (error) {
      console.error('❌ Error limpiando favoritos:', error);
      throw error;
    }
  };

  // Cargar favoritos al inicializar
  useEffect(() => {
    loadFavorites();
  }, []);

  // Efecto para guardar favoritos cuando cambie el estado (solo después de la carga inicial)
  useEffect(() => {
    if (!state.isLoading) {
      console.log('💾 FavoritesContext: Guardando favoritos automáticamente, cantidad:', state.items.length);
      saveFavorites(state.items);
    }
  }, [state.items]);

  return (
    <FavoritesContext.Provider value={{
      state,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      clearFavorites,
      loadFavorites,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

// Hook para usar el contexto
export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
